import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _sliced_to_array from "@swc/helpers/src/_sliced_to_array.mjs";
import _to_consumable_array from "@swc/helpers/src/_to_consumable_array.mjs";
import { isDevEnvironment } from "@core/configuration";
import { applyMiddleware, combineReducers, compose, legacy_createStore } from "redux";
import { composeWithDevTools } from "@redux-devtools/extension";
import promise from "redux-promise-middleware";
import { thunk } from "redux-thunk";
var warnInDev = function(message) {
    if (__APP_MODE__ === "development") {
        console.warn(message);
    }
};
var createReducerManager = function() {
    var initialReducers = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    // Create an object which maps keys to reducers
    var reducers = new Map();
    // Add a root reducer to prevent combineReducers from failing with an empty object
    var rootReducer = function() {
        var state = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        return state;
    };
    reducers.set("__root__", rootReducer);
    // add the initial reducers
    Object.entries(_object_spread({}, initialReducers)).forEach(function(param) {
        var _param = _sliced_to_array(param, 2), key = _param[0], reducer = _param[1];
        return reducers.set(key, reducer);
    });
    // Create the initial combinedReducer
    var combinedReducer = combineReducers(Object.fromEntries(reducers));
    // An array which is used to delete state keys when reducers are removed
    var keysToRemove = new Set();
    return {
        getReducerMap: function() {
            return Object.fromEntries(reducers);
        },
        reduce: function(state, action) {
            // If any reducers have been removed, clean up their state first
            if (keysToRemove.size > 0) {
                state = _object_spread({}, state);
                keysToRemove.forEach(function(key) {
                    delete state[key];
                });
                keysToRemove = new Set();
            }
            if (action.type === "RESET_STATE") {
                state = undefined;
            }
            // Delegate to the combined reducer
            return combinedReducer(state, action);
        },
        /**
         * Add one or many reducers to the store
         *
         * @param key either a string or ReducersMapObject
         * @param reducer if the first parameter is a string:` the reducer, undefined otherwise
         */ add: function(key, reducer) {
            if (!key) {
                throw new Error("You must specify a key.");
            }
            if (typeof key === "string" && reducers.get(key)) {
                warnInDev("The reducer ".concat(key, " already exists in the store. Skipping registration."));
                return;
            }
            if (typeof key === "string") {
                // Add the reducer to the reducer mapping
                reducers.set(key, reducer);
            } else {
                Object.entries(key).forEach(function(param) {
                    var _param = _sliced_to_array(param, 2), reducerKey = _param[0], reducerFunction = _param[1];
                    if (reducers.get(reducerKey)) {
                        warnInDev("The reducer ".concat(reducerKey, " already exists in the store. Skipping registration."));
                        return;
                    }
                    reducers.set(reducerKey, reducerFunction);
                });
            }
            // Generate a new combined reducer
            combinedReducer = combineReducers(Object.fromEntries(reducers));
        },
        /**
         * Remove one or many reducers from the store
         *
         * @param key a string or string[] of the reducers key(s) to remove
         */ remove: function(key) {
            if (!key) {
                throw new Error("You must specify a key.");
            }
            var keys = typeof key === "string" ? [
                key
            ] : key;
            keys.forEach(function(reducerKey) {
                if (!reducers.get(reducerKey)) {
                    throw new Error("The reducer ".concat(reducerKey, " does not exist in the store."));
                }
                // Remove it from the reducer mapping
                reducers.delete(reducerKey);
                // Add the key to the list of keys to clean up
                keysToRemove.add(reducerKey);
            });
            // Generate a new combined reducer
            combinedReducer = combineReducers(Object.fromEntries(reducers));
        }
    };
};
var middlewares = [
    thunk,
    promise
];
var composeEnhancers = isDevEnvironment() ? composeWithDevTools({
    name: "admin-ui"
}) : compose;
/**
 * Generates a new store with reducers and initial state.
 * In the admin-ui only one store should exist but this method can be useful in UTs
 *
 * @param reducers initial reducers to add to the store
 * @param initialState default state of the application
 */ export var generateStore = function(reducers, initialState) {
    var manager = createReducerManager(reducers);
    var store = legacy_createStore(manager.reduce, initialState, composeEnhancers(applyMiddleware.apply(void 0, _to_consumable_array(middlewares))));
    return {
        manager: manager,
        store: store
    };
};
var ref = generateStore({}), reducerManager = ref.manager, globalStore = ref.store;
/**
 * Return the Redux store of the application.
 * This method is generic, use the generic parameter to type the state
 *
 * @example const store = getStore<{myProperty: number}>(); // store.getState().myProperty will exist
 */ export var getStore = function() {
    return globalStore;
};
export var addReducer = reducerManager.add;
export var removeReducer = reducerManager.remove;

//# sourceMappingURL=store.js.map